(function () {
  "use strict";

  function initModernHero(cfg) {
    const root = document.getElementById(cfg.sid);
    if (!root) return;

    const slides = Array.from(root.querySelectorAll(".mhero__slide"));
    const contents = Array.from(root.querySelectorAll(".mhero__slide-content"));
    const dots = Array.from(root.querySelectorAll(".mhero__dot"));
    const wavePath = root.querySelector(".mhero__wave-path");
    const waveBorderPath = root.querySelector(".mhero__wave-path--border");
    const waveSvgEl = root.querySelector(".mhero__wave-svg");
    const copyLayer = root.querySelector(".mhero__copy-layer");
    const prevBtn = root.querySelector(".mhero__arrow-prev");
    const nextBtn = root.querySelector(".mhero__arrow-next");

    const COUNT = slides.length;
    const WAVE_INTENSITY = parseFloat(root.dataset.waveIntensity) || 1;
    const WAVE_BORDER_RAISE = 22; // viewBox units the border wave sits above the main wave
    const MID_LIFT = 35; // viewBox units the border wave's midpoint bulges above its ends
    const INTERVAL_MS = parseInt(root.dataset.interval, 10) || 6500;

    const WAVE_SHAPES = [
      { base: 96, amp: 28, freq: 1.05, phase: 0.25 },
      { base: 78, amp: 17, freq: 0.82, phase: 1.5 },
      { base: 108, amp: 34, freq: 1.34, phase: 2.7 },
    ];

    const WAVE_MODE = root.dataset.waveMode;
    const WAVE_VB_H = 200; // must match buildWavePath's viewBox height
    const WAVE_SAFETY_PX = 16; // guaranteed breathing room between content and the crest
    const MOBILE_WAVE_SCALE = 0.65; // dampens undulation on small screens so it reads calmer

    // Cached rather than querying matchMedia on every animation frame — only
    // re-derived when the breakpoint is actually crossed (see 'change' below).
    const mobileMql = window.matchMedia("(max-width: 749px)");
    let isMobile = mobileMql.matches;
    function isMobileViewport() {
      return isMobile;
    }

    const reducedMotionMql = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reducedMotion = reducedMotionMql.matches;

    function paintWave(p) {
      wavePath.setAttribute("d", buildWavePath(p, 0));
      if (waveBorderPath) {
        waveBorderPath.setAttribute(
          "d",
          buildWavePath({ ...p, base: p.base - WAVE_BORDER_RAISE }, MID_LIFT),
        );
      }
    }

    // Reserve the main wave's crest height (plus a safety margin) at the
    // bottom of the copy layer, so content never sits behind the wave and
    // still feels centered in the space above it rather than the full hero.
    let lastWaveParams = null;
    function applyCopyOffset(p) {
      lastWaveParams = p;
      const scale = isMobile ? MOBILE_WAVE_SCALE : 1;
      const svgH = (waveSvgEl && waveSvgEl.getBoundingClientRect().height) || 150;
      const crestY = p.base - p.amp * WAVE_INTENSITY * scale;
      const crestPx =
        Math.min(svgH, Math.max(0, (WAVE_VB_H - crestY) * (svgH / WAVE_VB_H))) + WAVE_SAFETY_PX;
      root.style.setProperty("--mhero-wave-crest", crestPx.toFixed(1) + "px");
    }

    function waveParamsAt(i) {
      if (WAVE_MODE !== "custom") return WAVE_SHAPES[i % WAVE_SHAPES.length];
      const el = slides[i];
      return {
        base: parseFloat(el.dataset.waveBase) || 96,
        amp: parseFloat(el.dataset.waveAmp) || 28,
        freq: parseFloat(el.dataset.waveFreq) || 1.05,
        phase: parseFloat(el.dataset.wavePhase) || 0.25,
      };
    }

    // Cardinal-spline smooth path through wave sample points
    function smoothPath(pts, W, H) {
      let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
      }
      return d + ` L ${W} ${H} L 0 ${H} Z`;
    }

    function buildWavePath(p, lift) {
      const W = 1440,
        H = 200,
        N = 6;
      const scale = isMobile ? MOBILE_WAVE_SCALE : 1;
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const t = i / N;
        const bulge = (lift || 0) * Math.sin(Math.PI * t) * scale; // 0 at both ends, peak at the midpoint
        pts.push({
          x: t * W,
          y:
            p.base +
            p.amp * WAVE_INTENSITY * scale * Math.sin(p.phase + t * Math.PI * 2 * p.freq) -
            bulge,
        });
      }
      return smoothPath(pts, W, H);
    }

    // Static hero — paint the wave once and stop
    if (COUNT <= 1) {
      paintWave(waveParamsAt(0));
      applyCopyOffset(waveParamsAt(0));
      return;
    }

    let index = 0;
    let timer = null;
    let raf = null;
    let textTimeout = null;
    let curWave = { ...WAVE_SHAPES[0] };

    // Autoplay is paused for any of three independent reasons at once —
    // hover, off-screen, or a live prefers-reduced-motion change — and only
    // resumes once none of them apply.
    const paused = { hover: false, hidden: false, motion: reducedMotion };
    function refreshAutoplay() {
      clearInterval(timer);
      const enabled =
        root.dataset.autoplay !== "false" && !paused.hover && !paused.hidden && !paused.motion;
      if (enabled) timer = setInterval(() => goTo(index + 1), INTERVAL_MS);
    }

    function animateWave(target) {
      const from = { ...curWave };
      const dur = 950,
        start = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      cancelAnimationFrame(raf);
      function step(now) {
        const k = Math.min(1, (now - start) / dur);
        const e = ease(k);
        curWave = {
          base: from.base + (target.base - from.base) * e,
          amp: from.amp + (target.amp - from.amp) * e,
          freq: from.freq + (target.freq - from.freq) * e,
          phase: from.phase + (target.phase - from.phase) * e,
        };
        paintWave(curWave);
        if (k < 1) raf = requestAnimationFrame(step);
      }
      raf = requestAnimationFrame(step);
    }

    // Set 'is-active' on exactly one element of a list. Authoritative rather
    // than a paired remove/add on a remembered previous index — a deferred swap
    // can be cancelled mid-flight by a fast second click, which would otherwise
    // leave the skipped slide's element stuck active.
    function setActive(list, i) {
      list.forEach((el, n) => el.classList.toggle("is-active", n === i));
    }

    function goTo(i) {
      const next = ((i % COUNT) + COUNT) % COUNT;
      if (next === index) return;

      // Background crossfade
      setActive(slides, next);

      // Content: fade whole copy-layer out, swap while invisible, fade back in
      copyLayer.classList.add("is-fading");
      clearTimeout(textTimeout);
      textTimeout = setTimeout(
        () => {
          setActive(contents, next);
          copyLayer.classList.remove("is-fading");
        },
        reducedMotion ? 0 : 360,
      );

      setActive(dots, next);

      const target = waveParamsAt(next);
      if (reducedMotion) {
        cancelAnimationFrame(raf);
        curWave = target;
        paintWave(curWave);
      } else {
        animateWave(target);
      }
      applyCopyOffset(target);

      index = next;
      refreshAutoplay();
    }

    // Re-paint at the current shape when the viewport crosses the mobile
    // breakpoint. matchMedia's 'change' event only fires on an actual
    // breakpoint crossing — unlike a raw 'resize' listener, it doesn't fire
    // when a mobile browser's address bar toggles mid-scroll (height-only).
    function handleViewportChange() {
      isMobile = mobileMql.matches;
      if (!lastWaveParams) return;
      if (!raf) paintWave(lastWaveParams);
      applyCopyOffset(lastWaveParams);
    }
    mobileMql.addEventListener("change", handleViewportChange);

    function handleMotionChange() {
      reducedMotion = reducedMotionMql.matches;
      paused.motion = reducedMotion;
      refreshAutoplay();
    }
    reducedMotionMql.addEventListener("change", handleMotionChange);

    let io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          paused.hidden = !entries[0].isIntersecting;
          refreshAutoplay();
        },
        { threshold: 0 },
      );
      io.observe(root);
    }

    prevBtn.addEventListener("click", () => goTo(index - 1));
    nextBtn.addEventListener("click", () => goTo(index + 1));
    dots.forEach((dot, i) => dot.addEventListener("click", () => goTo(i)));

    root.addEventListener("mouseenter", () => {
      paused.hover = true;
      refreshAutoplay();
    });
    root.addEventListener("mouseleave", () => {
      paused.hover = false;
      refreshAutoplay();
    });

    // Touch swipe
    let touchX = 0;
    root.addEventListener(
      "touchstart",
      (e) => {
        touchX = e.touches[0].clientX;
      },
      { passive: true },
    );
    root.addEventListener(
      "touchend",
      (e) => {
        const dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 40) goTo(dx < 0 ? index + 1 : index - 1);
      },
      { passive: true },
    );

    // Tear down listeners/observers that outlive a normal page load (matchMedia
    // 'change' listeners and IntersectionObserver persist until explicitly
    // removed) when Shopify's theme editor re-renders or removes this section.
    const rawSectionId = cfg.sid.replace(/^mhc-/, "");
    function onUnload(e) {
      if (!e.detail || e.detail.sectionId !== rawSectionId) return;
      clearInterval(timer);
      cancelAnimationFrame(raf);
      clearTimeout(textTimeout);
      mobileMql.removeEventListener("change", handleViewportChange);
      reducedMotionMql.removeEventListener("change", handleMotionChange);
      if (io) io.disconnect();
      document.removeEventListener("shopify:section:unload", onUnload);
    }
    document.addEventListener("shopify:section:unload", onUnload);

    // Init
    paintWave(curWave);
    applyCopyOffset(curWave);
    refreshAutoplay();
  }

  function processAll() {
    (Array.isArray(window.mheroConfigs) ? window.mheroConfigs : []).forEach(initModernHero);
    window.mheroConfigs = { push: initModernHero };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", processAll);
  } else {
    processAll();
  }
})();
